import { EditOutlined, LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { MenuProps } from 'antd/lib'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import countryList from 'react-select-country-list'

import {
  useStocksServiceDeleteProducerEndpoint,
  useStocksServiceGetProducerListEndpoint,
  useStocksServiceGetProducerListEndpointKey
} from 'src/api/queries'
import { ApiError, GetProducerListResponseItem } from 'src/api/requests'
import {
  DeleteConfirm,
  message,
  SearchBar,
  TableActionButtons
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, resetPagination } from 'src/utils'

import { ProducersModal } from './ProducersModal'

export const ProducersCard = () => {
  const { t } = useLocales()
  const navigate = useNavigate()
  const pagination = useTablePagination()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [currentId, setCurrentId] = useState<string>()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...(!!search && {
      searchTerm: search
    })
  }

  const { data, isLoading } =
    useStocksServiceGetProducerListEndpoint(queryParams)

  const { mutate } = useStocksServiceDeleteProducerEndpoint({
    onError: (e: ApiError) => formErrorHandler(e.body.errors),
    onSuccess: () => {
      message.success(t('medicine_register.producers.delete_success'))
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetProducerListEndpointKey]
      })
    }
  })

  const onToggle = () => setIsOpen((prev) => !prev)

  const onSearch = (val: string) => {
    setSearch(val)
    resetPagination(pagination)
  }

  const onCloseModal = () => {
    onToggle()
    setCurrentId(undefined)
  }

  const getActionItems = useMemo(
    () =>
      (id: string): MenuProps['items'] => {
        return [
          {
            key: 'edit',
            label: t('common.edit'),
            icon: <EditOutlined />,
            onClick: () => {
              onToggle()
              setCurrentId(id)
            }
          },
          {
            key: 'divider',
            type: 'divider'
          },
          {
            key: 'delete',
            label: (
              <DeleteConfirm
                title={t('medicine_register.producers.delete')}
                onConfirm={() => mutate({ id })}
              />
            ),
            danger: true
          }
        ]
      },
    [t]
  )

  const columns: ColumnsType<GetProducerListResponseItem> = [
    {
      key: 'name',
      title: t('medicine_register.producers.title'),
      dataIndex: 'name'
    },
    {
      key: 'country',
      title: t('common.country'),
      render: (_, { country }) =>
        country ? countryList().getLabel(country) : '-'
    },
    {
      key: 'actions',
      width: 64,
      render: ({ id }) => (
        <TableActionButtons items={getActionItems(id)} justify="center" />
      )
    }
  ]

  return (
    <Card>
      <Flex vertical gap={16}>
        <Flex gap={16}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate(paths.medicinesRegister.root)}
          >
            {t('common.back')}
          </Button>

          <SearchBar className="flex-1" onSearch={onSearch} />

          <Button onClick={onToggle} type="primary">
            {t('common.add')}
          </Button>
        </Flex>

        <Table
          rowKey="id"
          loading={isLoading}
          bordered
          columns={columns}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>

      {isOpen && <ProducersModal id={currentId} onCancel={onCloseModal} />}
    </Card>
  )
}
